import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Store, 
  CheckCircle, 
  Clock, 
  Users, 
  TrendingUp, 
  Shield, 
  DollarSign,
  BarChart3,
  Truck,
  Headphones,
  Star,
  ArrowRight,
  Target,
  Globe,
  Zap,
  Award,
  XCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/hooks/useLanguage';
import { useCMSContent } from '@/hooks/useCMSContent';

const BecomeVendor = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { content: cmsContent, loading: contentLoading } = useCMSContent('become-vendor-page', language);
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    business_name: '',
    business_description: '',
    business_address: '',
    business_phone: '',
    business_email: '',
    business_type: '',
    business_entity_type: 'individual' as 'individual' | 'company',
    experience: '',
    motivation: '',
    accepts_terms: false
  });
  const [identityDocument, setIdentityDocument] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Fetch user roles to check if user is already a vendor
  useEffect(() => {
    const fetchUserRoles = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);
        
        if (error) {
          console.warn('Error fetching user roles:', error);
          return;
        }
        
        setUserRoles(data || []);
      } catch (error) {
        console.warn('Error in fetchUserRoles:', error);
      }
    };

    fetchUserRoles();
  }, [user?.id]);

  const getDefaultContent = () => ({
    hero: {
      badge: 'Plateforme #1 en RDC',
      title: 'Vendez sur Nazandoo et',
      titleHighlight: 'développez votre business',
      description: 'Rejoignez plus de 10 000 vendeurs qui font confiance à Nazandoo pour développer leur activité. Configuration en 24h, commission de seulement 8%, paiements garantis sous 24h.',
      ctaMain: 'Commencer à vendre maintenant',
      ctaSecondary: 'Voir une démonstration',
      demoLink: 'https://calendly.com'
    },
    stats: [
      { value: '500K+', label: 'Produits vendus' },
      { value: '98%', label: 'Vendeurs satisfaits' },
      { value: '15M$', label: "Chiffre d'affaires généré" },
      { value: '24h', label: 'Délai moyen de paiement' }
    ],
    benefits: [
      {
        title: "Revenus jusqu'à 100 000$ par mois",
        description: 'Nos top vendeurs génèrent des revenus mensuels exceptionnels',
        highlight: 'Commission de seulement 8%'
      },
      {
        title: '2M+ clients actifs',
        description: 'Accédez à notre base de clients grandissante en RDC',
        highlight: 'Croissance de 300% par an'
      },
      {
        title: "Expansion à travers l'Afrique",
        description: 'Votre vitrine sera bientôt disponible dans 15 pays',
        highlight: 'Lancement prévu 2024'
      },
      {
        title: 'Configuration en moins de 24h',
        description: 'Commencez à vendre dès demain avec notre processus simplifié',
        highlight: 'Validation rapide'
      }
    ],
    tools: [
      {
        title: 'Analytics Avancés',
        description: 'Tableaux de bord détaillés sur vos ventes, clients et performances'
      },
      {
        title: 'Publicité Ciblée',
        description: 'Boostez vos ventes avec nos outils de marketing intégrés'
      },
      {
        title: 'Logistique Simplifiée',
        description: 'Notre réseau de points relais gère la livraison pour vous'
      },
      {
        title: 'Support Dédié',
        description: 'Équipe d\'assistance disponible 7j/7 pour vous accompagner'
      }
    ],
    testimonials: [
      {
        name: 'Marie Kabila',
        business: 'Mode & Accessoires',
        revenue: '45 000$ /mois',
        quote: "En 6 mois sur Nazandoo, j'ai multiplié mes ventes par 10. La plateforme est intuitive et les clients font confiance.",
        rating: 5,
        image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face'
      },
      {
        name: 'Jean-Paul Mukendi',
        business: 'Électronique',
        revenue: '78 000$ /mois',
        quote: "Nazandoo m'a permis d'atteindre des clients dans toute la RDC. Les outils d'analyse sont exceptionnels.",
        rating: 5,
        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face'
      },
      {
        name: 'Grace Mbuyi',
        business: 'Artisanat Local',
        revenue: '32 000$ /mois',
        quote: "Je vendais uniquement au marché central. Maintenant, mes créations touchent l'Afrique entière !",
        rating: 5,
        image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face'
      }
    ],
    sections: {
      benefits: {
        title: 'Pourquoi choisir Nazandoo ?',
        description: 'Des milliers de vendeurs nous font confiance pour développer leur business. Découvrez pourquoi Nazandoo est la plateforme de choix en RDC.'
      },
      tools: {
        title: 'Outils pour maximiser vos ventes',
        description: "Profitez d'une suite complète d'outils professionnels pour gérer et développer votre activité."
      },
      testimonials: {
        title: 'Success Stories',
        description: 'Découvrez comment nos vendeurs ont transformé leur business avec Nazandoo.'
      },
      form: {
        title: 'Rejoignez Nazandoo dès aujourd\'hui',
        description: 'Remplissez ce formulaire et commencez à vendre en moins de 24h'
      },
      process: {
        title: 'Comment ça marche ?',
        description: 'Processus simple et rapide pour commencer à vendre'
      },
      finalCta: {
        title: 'Prêt à transformer votre business ?',
        description: 'Rejoignez les milliers de vendeurs qui font confiance à Nazandoo',
        ctaMain: 'Commencer maintenant',
        ctaSecondary: 'Parler à un conseiller',
        phone: '+243123456789'
      }
    }
  });

  const pageContent = cmsContent?.content || getDefaultContent();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // L'utilisateur DOIT être connecté
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez créer un compte et vous connecter avant de soumettre une candidature vendeur.",
        variant: "destructive"
      });
      return;
    }

    // Validation des champs business
    if (!formData.business_name || !formData.business_type || !formData.business_address || 
        !formData.business_phone || !formData.business_email) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    if (!formData.accepts_terms) {
      toast({
        title: "Erreur",
        description: "Veuillez accepter les conditions générales",
        variant: "destructive"
      });
      return;
    }

    if (!identityDocument) {
      toast({
        title: "Erreur",
        description: "Veuillez joindre une pièce d'identité",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const userId = user.id;

      // Upload identity document
      let documentUrl = '';
      if (identityDocument) {
        setUploading(true);
        const fileExt = identityDocument.name.split('.').pop();
        const fileName = `${userId}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('vendor-documents')
          .upload(fileName, identityDocument);

        if (uploadError) throw uploadError;

        // Stocker le chemin du fichier (pas l'URL publique car bucket privé)
        documentUrl = fileName;
        setUploading(false);
      }

      // Create vendor application
      const applicationData = {
        user_id: userId,
        business_name: formData.business_name,
        business_description: formData.business_description,
        business_address: formData.business_address,
        business_phone: formData.business_phone,
        business_email: formData.business_email,
        business_type: formData.business_type,
        business_entity_type: formData.business_entity_type,
        identity_document_url: documentUrl,
        experience: formData.experience || null,
        motivation: formData.motivation || null,
        status: 'pending'
      };
      
      const { data: insertedApplication, error: applicationError } = await supabase
        .from('vendor_applications')
        .insert(applicationData)
        .select()
        .single();

      if (applicationError) {
        throw applicationError;
      }

      // Insert vendor role in user_roles table
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role: 'vendor'
        });

      if (roleError && !roleError.message.includes('duplicate')) throw roleError;

      // Update vendor_status in profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          vendor_status: 'pending'
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Envoyer notification email à l'admin
      try {
        await supabase.functions.invoke('send-admin-vendor-application', {
          body: {
            candidateName: formData.business_name || user?.full_name || 'Candidat',
            candidateEmail: user?.email || formData.business_email,
            businessName: formData.business_name,
            businessType: formData.business_entity_type,
            applicationId: userId,
          }
        });
      } catch (emailError) {
        // Ne pas bloquer la soumission si l'email échoue
      }

      // Envoyer email de confirmation au vendeur
      try {
        await supabase.functions.invoke('send-vendor-confirmation', {
          body: {
            vendorEmail: user?.email || formData.business_email,
            vendorName: user?.full_name || formData.business_name || 'Candidat',
            businessName: formData.business_name,
          }
        });
      } catch (emailError) {
        // Ne pas bloquer la soumission si l'email échoue
      }

      // Nettoyer le brouillon sauvegardé
      localStorage.removeItem('vendor_application_draft');

      toast({
        title: "Demande envoyée avec succès !",
        description: "Votre demande de devenir vendeur est en cours d'examen. Nous vous contacterons bientôt."
      });

      // Reset form
      setFormData({
        business_name: '',
        business_description: '',
        business_address: '',
        business_phone: '',
        business_email: '',
        business_type: '',
        business_entity_type: 'individual',
        experience: '',
        motivation: '',
        accepts_terms: false
      });
      setIdentityDocument(null);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const content = pageContent || getDefaultContent();
  
  const benefitIcons = [DollarSign, Users, Globe, Zap];
  const benefits = content.benefits?.map((benefit: any, index: number) => ({
    ...benefit,
    icon: benefitIcons[index % benefitIcons.length]
  })) || [];

  const toolIcons = [BarChart3, Target, Truck, Headphones];
  const tools = content.tools?.map((tool: any, index: number) => ({
    ...tool,
    icon: toolIcons[index % toolIcons.length]
  })) || [];

  const testimonials = content.testimonials || [];

  const stats = content.stats || [];

  // Check if user is already a vendor (through various means)
  const isVendorInRoles = userRoles.some(role => role.role === 'vendor');
  const hasApplied = profile?.vendor_status === 'pending' || 
                     profile?.vendor_status === 'approved' || 
                     profile?.vendor_status === 'rejected' ||
                     profile?.system_role === 'vendor' ||
                     isVendorInRoles; // Vérifie dans user_roles

  const isAlreadyVendor = profile?.vendor_status === 'approved' || 
                         profile?.system_role === 'vendor' || 
                         isVendorInRoles;

  // Scroll to form function
  const scrollToForm = () => {
    const formElement = document.getElementById('vendor-application-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (contentLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-primary/5 via-background to-primary/5 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="container relative">
            <div className="max-w-4xl mx-auto text-center">
              {/* Message pour vendeurs déjà approuvés */}
              {hasApplied && isAlreadyVendor && (
                <Card className="mb-8 border-green-200 bg-green-50">
                  <CardContent className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4 text-green-800">
                      {profile?.system_role === 'super_admin' ? 'Accès administrateur' : 'Félicitations ! Vous êtes déjà vendeur'}
                    </h2>
                    <p className="text-lg text-green-700 mb-8 max-w-2xl mx-auto">
                      {profile?.system_role === 'super_admin' 
                        ? 'Vous avez un accès administrateur avec privilèges vendeur.'
                        : 'Votre compte vendeur est actif. Accédez à votre espace pour gérer vos produits et commandes.'
                      }
                    </p>
                    <Button size="lg" className="bg-green-600 hover:bg-green-700" onClick={() => navigate('/vendor-account')}>
                      <Store className="mr-2 h-5 w-5" />
                      Accéder à mon espace vendeur
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              {/* Message pour candidatures en attente */}
              {hasApplied && profile?.vendor_status === 'pending' && !isAlreadyVendor && (
                <Card className="mb-8 border-amber-200 bg-amber-50">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                        <Clock className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Candidature en cours de validation</h3>
                        <p className="text-sm text-muted-foreground">
                          Notre équipe examine votre dossier. Vous recevrez une réponse sous 24-48h.
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-amber-300 text-amber-700">
                      En attente
                    </Badge>
                  </CardContent>
                </Card>
              )}
              
              {/* Message pour candidatures rejetées */}
              {hasApplied && profile?.vendor_status === 'rejected' && !isAlreadyVendor && (
                <Card className="mb-8 border-red-200 bg-red-50">
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <XCircle className="h-6 w-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-red-800">Candidature non approuvée</h3>
                        <p className="text-sm text-red-700">
                          Votre candidature n'a pas pu être approuvée cette fois. Vous pouvez soumettre une nouvelle demande.
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-red-300 text-red-700">
                      Non approuvée
                    </Badge>
                  </CardContent>
                </Card>
              )}

              <div className="mb-8">
                <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                  <Award className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">{content.hero?.badge}</span>
                </div>
                
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent">
                  {content.hero?.title}<br />
                  <span className="text-primary">{content.hero?.titleHighlight}</span>
                </h1>
                
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                  {content.hero?.description}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {!hasApplied && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-gradient-primary text-lg px-8 py-4" onClick={scrollToForm}>
                    <Store className="mr-2 h-5 w-5" />
                    {content.hero?.ctaMain}
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 py-4" onClick={() => window.open(content.hero?.demoLink || 'https://calendly.com', '_blank')}>
                    {content.hero?.ctaSecondary}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Content visible uniquement aux non-vendeurs */}
        {!(hasApplied && isAlreadyVendor) && (
          <>
            {/* Benefits Section */}
            <section className="py-20">
              <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                {content.sections?.benefits?.title}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {content.sections?.benefits?.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {benefits.map((benefit, index) => (
                <Card key={index} className="p-8 hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-card">
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <Badge className="bg-success/10 text-success hover:bg-success/20">
                        {benefit.highlight}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="py-20 bg-muted/30">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                {content.sections?.tools?.title}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {content.sections?.tools?.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tools.map((tool, index) => (
                <Card key={index} className="text-center p-6 hover:shadow-card transition-shadow">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <tool.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{tool.title}</h3>
                  <p className="text-sm text-muted-foreground">{tool.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                {content.sections?.testimonials?.title}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {content.sections?.testimonials?.description}
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="p-6 hover:shadow-elegant transition-shadow">
                  <div className="flex items-center space-x-4 mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold">{testimonial.name}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.business}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4 italic">
                    "{testimonial.quote}"
                  </p>
                  
                  <div className="border-t pt-4">
                    <div className="text-lg font-bold text-primary">{testimonial.revenue}</div>
                    <div className="text-xs text-muted-foreground">Revenus mensuels</div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-20 bg-muted/30">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              {!hasApplied ? (
                <Card id="vendor-application-form">
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl">{content.sections?.form?.title}</CardTitle>
                    <CardDescription className="text-lg">
                      {content.sections?.form?.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-8">
                      {/* Message for non-logged users */}
                      {!user && (
                        <>
                          <Card className="border-amber-200 bg-amber-50/50 mb-6">
                            <CardContent className="p-6">
                              <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Shield className="h-6 w-6 text-amber-600" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-semibold text-lg mb-2">Connexion requise</h3>
                                  <p className="text-sm text-muted-foreground mb-4">
                                    Pour soumettre une candidature vendeur, vous devez d'abord créer un compte et vous connecter.
                                  </p>
                                  <div className="flex gap-3">
                                    <Button onClick={() => navigate('/auth')} className="bg-gradient-primary">
                                      Se connecter / Créer un compte
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                          <Separator className="my-6" />
                        </>
                      )}

                      {/* Business Information */}
                      <div className="space-y-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">1</span>
                          </div>
                          <h3 className="text-xl font-semibold">Informations sur votre entreprise</h3>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6 pl-11">
                          <div>
                            <Label htmlFor="business_entity_type">Type de vendeur *</Label>
                            <select
                              id="business_entity_type"
                              value={formData.business_entity_type}
                              onChange={(e) => setFormData(prev => ({ ...prev, business_entity_type: e.target.value as 'individual' | 'company' }))}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mt-2"
                              required
                            >
                              <option value="individual">Particulier</option>
                              <option value="company">Société</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="business_name">
                              {formData.business_entity_type === 'company' ? "Nom de l'entreprise *" : "Nom complet *"}
                            </Label>
                            <Input
                              id="business_name"
                              value={formData.business_name}
                              onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                              placeholder={formData.business_entity_type === 'company' ? "Ex: TechKin Electronics" : "Ex: Jean Mukendi"}
                              required
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label htmlFor="business_type">Catégorie de produits *</Label>
                            <Input
                              id="business_type"
                              placeholder="Ex: Électronique, Mode, Artisanat..."
                              value={formData.business_type}
                              onChange={(e) => setFormData(prev => ({ ...prev, business_type: e.target.value }))}
                              required
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label htmlFor="identity_document">
                              Pièce d'identité * {uploading && <span className="text-muted-foreground text-xs">(Upload en cours...)</span>}
                            </Label>
                            <Input
                              id="identity_document"
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                setIdentityDocument(file);
                              }}
                              required
                              className="mt-2"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {formData.business_entity_type === 'company' 
                                ? "RCCM, NIF ou autre document officiel" 
                                : "Carte d'identité, passeport ou permis de conduire"}
                            </p>
                          </div>
                        </div>

                        <div className="pl-11">
                          <Label htmlFor="business_description">Décrivez votre activité *</Label>
                          <Textarea
                            id="business_description"
                            placeholder="Décrivez vos produits, votre expertise, votre marché cible..."
                            value={formData.business_description}
                            onChange={(e) => setFormData(prev => ({ ...prev, business_description: e.target.value }))}
                            rows={4}
                            required
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <Separator />

                      {/* Contact Information */}
                      <div className="space-y-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">2</span>
                          </div>
                          <h3 className="text-xl font-semibold">Informations de contact</h3>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6 pl-11">
                          <div>
                            <Label htmlFor="business_address">
                              {formData.business_entity_type === 'individual' ? 'Adresse *' : 'Adresse de l\'entreprise *'}
                            </Label>
                            <Input
                              id="business_address"
                              value={formData.business_address}
                              onChange={(e) => setFormData(prev => ({ ...prev, business_address: e.target.value }))}
                              placeholder="Avenue Tombalbaye, Gombe, Kinshasa"
                              required
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label htmlFor="business_phone">Téléphone professionnel *</Label>
                            <Input
                              id="business_phone"
                              type="tel"
                              value={formData.business_phone}
                              onChange={(e) => setFormData(prev => ({ ...prev, business_phone: e.target.value }))}
                              placeholder="+243 XX XXX XXXX"
                              required
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label htmlFor="business_email">Email professionnel *</Label>
                            <Input
                              id="business_email"
                              type="email"
                              value={formData.business_email}
                              onChange={(e) => setFormData(prev => ({ ...prev, business_email: e.target.value }))}
                              placeholder="contact@monentreprise.com"
                              required
                              className="mt-2"
                            />
                          </div>
                        </div>
                      </div>


                      {/* Terms */}
                      <div className="space-y-6">
                        <div className="flex items-start space-x-3 pl-11">
                          <Checkbox
                            id="accepts_terms"
                            checked={formData.accepts_terms}
                            onCheckedChange={(checked) => 
                              setFormData(prev => ({ ...prev, accepts_terms: checked as boolean }))
                            }
                            className="mt-1"
                          />
                          <div className="text-sm">
                            <Label htmlFor="accepts_terms" className="cursor-pointer font-normal">
                              J'accepte les{' '}
                              <Link to="/terms" className="text-primary hover:underline font-medium">
                                conditions générales pour les vendeurs
                              </Link>{' '}
                              et la{' '}
                              <Link to="/privacy" className="text-primary hover:underline font-medium">
                                politique de confidentialité
                              </Link>
                            </Label>
                            <p className="text-muted-foreground mt-1">
                              En créant votre compte vendeur, vous acceptez de payer une commission de 8% sur chaque vente.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-center pt-6">
                        <Button 
                          type="submit" 
                          size="lg"
                          className="bg-gradient-primary text-lg px-12 py-4" 
                          disabled={loading || !user}
                        >
                          {!user ? (
                            "Connectez-vous pour soumettre votre candidature"
                          ) : loading ? (
                            "Validation en cours..."
                          ) : (
                            <>
                              <Store className="mr-2 h-5 w-5" />
                              Créer mon compte vendeur
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="text-center py-16">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-10 w-10 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Candidature soumise avec succès !</h2>
                    <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                      Nous examinons votre dossier et vous contacterons dans les 24-48h. 
                      Préparez vos premiers produits en attendant !
                    </p>
                    <Button size="lg" variant="outline" onClick={() => navigate('/')}>
                      Retour à l'accueil
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-20">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{content.sections?.process?.title}</h2>
                <p className="text-lg text-muted-foreground">
                  {content.sections?.process?.description}
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center relative">
                  <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-glow">
                    <span className="font-bold text-primary-foreground text-lg">1</span>
                  </div>
                  <h4 className="font-semibold mb-2">Candidature</h4>
                  <p className="text-sm text-muted-foreground">
                    Soumettez votre formulaire en 5 minutes
                  </p>
                  {/* Connection line */}
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary to-muted -translate-x-4 -z-10"></div>
                </div>
                
                <div className="text-center relative">
                  <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-glow">
                    <span className="font-bold text-primary-foreground text-lg">2</span>
                  </div>
                  <h4 className="font-semibold mb-2">Validation Express</h4>
                  <p className="text-sm text-muted-foreground">
                    Validation en moins de 24h
                  </p>
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary to-muted -translate-x-4 -z-10"></div>
                </div>
                
                <div className="text-center relative">
                  <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-glow">
                    <span className="font-bold text-primary-foreground text-lg">3</span>
                  </div>
                  <h4 className="font-semibold mb-2">Configuration</h4>
                  <p className="text-sm text-muted-foreground">
                    Ajoutez vos premiers produits
                  </p>
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary to-muted -translate-x-4 -z-10"></div>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-success-foreground" />
                  </div>
                  <h4 className="font-semibold mb-2">Commencez à vendre</h4>
                  <p className="text-sm text-muted-foreground">
                    Votre boutique est en ligne !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

            {/* CTA Final */}
            <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
              <div className="container">
                <div className="max-w-4xl mx-auto text-center">
                  <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                    {content.sections?.finalCta?.title}
                  </h2>
                  <p className="text-xl text-muted-foreground mb-8">
                    {content.sections?.finalCta?.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button size="lg" className="bg-gradient-primary text-lg px-8 py-4" onClick={scrollToForm}>
                      <Store className="mr-2 h-5 w-5" />
                      {content.sections?.finalCta?.ctaMain}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button size="lg" variant="outline" className="text-lg px-8 py-4" onClick={() => window.open(`tel:${content.sections?.finalCta?.phone}`, '_self')}>
                      <Headphones className="mr-2 h-5 w-5" />
                      {content.sections?.finalCta?.ctaSecondary}
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BecomeVendor;